import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { Card, Field, ObjectEditor, SaveBtn, StatusCard } from "@/components/admin/challenge/ChallengeAdminParts";
import { ChallengeFunnel, ChallengeTestTools } from "@/components/admin/challenge/ChallengeAdminInsights";

const DAY_FIELDS = [["title", "Titel"], ["impuls", "Kopfzeile (Impuls)"], ["frage", "Aufgabe / Text", true], ["placeholder", "Platzhalter im Eingabefeld"], ["video_url", "Vimeo-Link"]];
const WRAP_FIELDS = [["title", "Titel"], ["text", "Text", true], ["video_url", "Vimeo-Link (Aufzeichnung)"], ["cta_label", "Button-Text"], ["note", "Hinweis unter dem Button (z. B. 39 € ab Tag 8)"]];
const REMINDER_FIELDS = [["subject_day", "Betreff Tag-Mail ({tag}, {vorname})"], ["body_day", "Text Tag-Mail", true], ["button_day", "Button Tag-Mail"], ["subject_wrapup", "Betreff Wrap-up-Mail"], ["body_wrapup", "Text Wrap-up-Mail", true], ["button_wrapup", "Button Wrap-up-Mail"]];
const JOIN_FIELDS = [["headline", "Überschrift"], ["text", "Text", true], ["cta_label", "Button-Text"]];

const CheckoutCard = ({ cfg, onSave }) => {
  const [url, setUrl] = useState(cfg.checkout_url || "");
  return (
    <Card title="Payment-Schritt (nach dem Wrap-up)" testId="ch2-checkout-card">
      <Field label="Ablefy-Checkout-URL (1 € für 7 Tage Pro, danach 39 €/Monat)" value={url} onChange={setUrl} testId="ch2-checkout-url" placeholder="leer = Standard-Checkout aus der Server-Konfiguration" />
      <p className="text-xs text-slate-500" data-testid="ch2-checkout-effective">{cfg.checkout_url_effective ? "Checkout ist konfiguriert." : "Kein Checkout konfiguriert."} Die E-Mail wird automatisch vorausgefüllt.</p>
      <SaveBtn onClick={() => onSave({ checkout_url: url.trim() })} testId="ch2-checkout-save" />
    </Card>
  );
};

const ReminderCard = ({ cfg, onSave }) => (
  <div className="space-y-2" data-testid="ch2-reminders-card">
    <label className="flex items-center gap-3 text-sm bg-white border border-slate-200 rounded-xl px-5 py-3">
      <input type="checkbox" checked={!!cfg.reminders.enabled} onChange={(e) => onSave({ reminders: { enabled: e.target.checked } })} data-testid="ch2-reminders-enabled" className="w-4 h-4" />
      Erinnerungsmails aktiv (Tag 2–5 sobald freigeschaltet, Wrap-up nach 5 × 24 h, mit Login-Link und Abmeldung)
    </label>
    <ObjectEditor title="Erinnerungsmails" value={cfg.reminders} fields={REMINDER_FIELDS} onSave={(v) => onSave({ reminders: v })} testId="ch2-reminders" />
  </div>
);

export default function AdminChallenge() {
  const [cfg, setCfg] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [day, setDay] = useState("1");

  const load = useCallback(async () => {
    const [c, f] = await Promise.all([api.get("/admin/challenge2/config"), api.get("/admin/challenge2/funnel")]);
    setCfg(c.data);
    setFunnel(f.data);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Konfiguration konnte nicht geladen werden.")); }, [load]);

  const save = async (patch) => {
    try {
      await api.put("/admin/challenge2/config", patch);
      await load();
      toast.success("Gespeichert");
    } catch (_e) {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-challenge-page">
        <header>
          <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">Admin · Challenge</p>
          <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold text-slate-900">5-Tage-Challenge (individueller Start)</h1>
          <p className="mt-1 text-sm text-slate-500">Kostenloser Einstieg · Tag 1 sofort, dann alle 24 Stunden · Wrap-up · danach 1-€-Test. Der Durchgang oct26 ist eingefroren.</p>
        </header>
        {cfg && funnel && (
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="space-y-5">
              <StatusCard cfg={cfg} onSave={save} />
              <ObjectEditor key={`join-${cfg.updated_at}`} title="Einstiegsseite" value={cfg.join} fields={JOIN_FIELDS} onSave={(v) => save({ join: v })} testId="ch2-join" />
              <Card title="Tagesinhalte" testId="ch2-days-card">
                <div className="flex gap-2">
                  {["1", "2", "3", "4", "5"].map((d) => (
                    <button key={d} type="button" onClick={() => setDay(d)} data-testid={`ch2-day-tab-${d}`}
                      className={`rounded-full px-3 py-1 text-sm font-semibold border ${day === d ? "bg-[#F3651D] text-white border-[#F3651D]" : "bg-white border-slate-300"}`}>Tag {d}</button>
                  ))}
                </div>
                <ObjectEditor key={`day-${day}-${cfg.updated_at}`} title={`Tag ${day}`} value={cfg.days[day]} fields={DAY_FIELDS}
                  onSave={(v) => save({ days: { [day]: v } })} testId={`ch2-day-${day}`} />
              </Card>
            </div>
            <div className="space-y-5">
              <ObjectEditor key={`wrap-${cfg.updated_at}`} title="Wrap-up (ab Tag 5)" value={cfg.wrapup} fields={WRAP_FIELDS} onSave={(v) => save({ wrapup: v })} testId="ch2-wrapup" />
              <CheckoutCard key={`co-${cfg.updated_at}`} cfg={cfg} onSave={save} />
              <ReminderCard key={`rem-${cfg.updated_at}`} cfg={cfg} onSave={save} />
              <ChallengeFunnel funnel={funnel} />
              <ChallengeTestTools />
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
